import { useState, type CSSProperties, type MouseEvent } from "react";
import { useApp } from "@/contexts/AppContext";
import { Book, Mail, Phone, MapPin, Globe } from "lucide-react";

interface FooterProps {
  navigate?: (page: string) => void;
}

export function Footer({ navigate }: FooterProps = {}) {
  const { t, activeTheme } = useApp();
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const addressMapHref =
    "https://www.google.com/maps/search/?api=1&query=Universitat%20Passau%20Fakultat%20fur%20Informatik%20und%20Mathematik%20Innstrasse%2043%2094032%20Passau";
  const phoneHref = "tel:+498515093306";
  const email = t("legalNotice.email");
  const emailHref = `mailto:${email}`;
  const isDark = activeTheme.tone === "dark";
  const footerBackground = isDark
    ? "color-mix(in srgb, var(--background) 94%, var(--accent))"
    : "color-mix(in srgb, var(--foreground) 80%, color-mix(in srgb, var(--background) 10%, var(--accent)))";
  const footerText = isDark
    ? "color-mix(in srgb, var(--foreground) 82%, var(--background))"
    : "color-mix(in srgb, var(--foreground) 20%, var(--background))";
  const footerMuted = isDark
    ? "color-mix(in srgb, var(--foreground) 72%, var(--background))"
    : "color-mix(in srgb, var(--foreground) 20%, var(--background))";
  const footerLink = isDark
    ? "color-mix(in srgb, var(--foreground) 80%, var(--background))"
    : "color-mix(in srgb, var(--foreground) 20%, var(--background))";
  const footerDivider = isDark
    ? `color-mix(in srgb, ${footerBackground} 35%, var(--background))`
    : `color-mix(in srgb, ${footerBackground} 25%, var(--foreground))`;
  const footerStyle = {
    backgroundColor: footerBackground,
    color: footerText,
  } as CSSProperties;
  const footerHeadingStyle = { color: footerText } as const;
  const footerBrandStyle = { color: "var(--heading-color)" } as const;
  const footerMutedTextStyle = {
    color: footerMuted,
  } as const;
  const footerLinkBaseColor = footerLink;
  const footerLinkHoverColor = "var(--primary)";
  const footerLinkStyle = (key: string): CSSProperties => ({
    color: hoveredLink === key ? footerLinkHoverColor : footerLinkBaseColor,
  });
  const footerDividerStyle = {
    borderColor: footerDivider,
  } as const;
  const footerIconBadgeStyle = {
    backgroundColor: "var(--primary)",
    color: "var(--primary-foreground)",
  } as const;

  const handleNavigate = (page: string) => (e: MouseEvent) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    e.preventDefault();
    if (navigate) {
      navigate(page);
    }
  };
  const handleLinkEnter = (key: string) => () => setHoveredLink(key);
  const handleLinkLeave = () => setHoveredLink(null);

  return (
    <footer className="mt-auto" style={footerStyle}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo und Beschreibung */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg" style={footerIconBadgeStyle}>
                <Book className="w-6 h-6" />
              </div>
              <div>
                <h3
                  className="text-base md:text-lg"
                  style={footerBrandStyle}
                >
                  {t("header.title")}
                </h3>
                <p className="text-xs" style={footerMutedTextStyle}>
                  {t("header.subtitle")}
                </p>
              </div>
            </div>
            <p className="text-sm max-w-md" style={footerMutedTextStyle}>
              {t("footer.aboutText")}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="mb-4" style={footerHeadingStyle}>
              {t("footer.legal")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="/legal-notice"
                  onClick={handleNavigate("impressum")}
                  onMouseEnter={handleLinkEnter("impressum")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("impressum")}
                  onBlur={handleLinkLeave}
                  className="transition-colors"
                  style={footerLinkStyle("impressum")}
                >
                  {t("footer.legalNotice")}
                </a>
              </li>
              <li>
                <a
                  href="/privacy"
                  onClick={handleNavigate("datenschutz")}
                  onMouseEnter={handleLinkEnter("datenschutz")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("datenschutz")}
                  onBlur={handleLinkLeave}
                  className="transition-colors"
                  style={footerLinkStyle("datenschutz")}
                >
                  {t("footer.privacy")}
                </a>
              </li>
              <li>
                <a
                  href="/terms-of-service"
                  onClick={handleNavigate("nutzungsbedingungen")}
                  onMouseEnter={handleLinkEnter("nutzungsbedingungen")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("nutzungsbedingungen")}
                  onBlur={handleLinkLeave}
                  className="transition-colors"
                  style={footerLinkStyle("nutzungsbedingungen")}
                >
                  {t("footer.terms")}
                </a>
              </li>
              <li>
                <a
                  href="/help"
                  onClick={handleNavigate("hilfe")}
                  onMouseEnter={handleLinkEnter("hilfe")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("hilfe")}
                  onBlur={handleLinkLeave}
                  className="transition-colors"
                  style={footerLinkStyle("hilfe")}
                >
                  {t("footer.help")}
                </a>
              </li>
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="mb-4" style={footerHeadingStyle}>
              {t("footer.contact")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <a
                  href={addressMapHref}
                  target="_blank"
                  rel="noreferrer"
                  className="transition-colors hover:text-[var(--primary)]"
                  style={footerLinkStyle("contact-address")}
                  onMouseEnter={handleLinkEnter("contact-address")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("contact-address")}
                  onBlur={handleLinkLeave}
                >
                  Universität Passau
                  <br />
                  Fakultät für Informatik und Mathematik
                  <br />
                  Innstraße 43
                  <br />
                  94032 Passau
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a
                  href={phoneHref}
                  className="transition-colors hover:text-[var(--primary)]"
                  style={footerLinkStyle("contact-phone")}
                  onMouseEnter={handleLinkEnter("contact-phone")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("contact-phone")}
                  onBlur={handleLinkLeave}
                >
                  +49(0)851 509-3306
                </a>
              </li>
              <li className="flex items-start gap-2 min-w-0">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a
                  href={emailHref}
                  className="transition-colors hover:text-[var(--primary)]"
                  style={{ ...footerLinkStyle("contact-email"), overflowWrap: "anywhere" }}
                  onMouseEnter={handleLinkEnter("contact-email")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("contact-email")}
                  onBlur={handleLinkLeave}
                >
                  {email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Globe className="w-4 h-4 flex-shrink-0" />
                <a
                  href="https://www.fim.uni-passau.de/data-science"
                  target="_blank"
                  rel="noreferrer"
                  className="transition-colors hover:text-[var(--primary)]"
                  style={footerLinkStyle("fim-link")}
                  onMouseEnter={handleLinkEnter("fim-link")}
                  onMouseLeave={handleLinkLeave}
                  onFocus={handleLinkEnter("fim-link")}
                  onBlur={handleLinkLeave}
                >
                  www.fim.uni-passau.de/data-science
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          className="border-t mt-8 pt-8 text-sm text-center"
          style={footerDividerStyle}
        >
          <p style={footerMutedTextStyle}>{t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  );
}
